import { TreeModule } from '@ali-hm/angular-tree-component';
import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter, withComponentInputBinding } from '@angular/router';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import {
    AwareAuthHttpInterceptor,
    AwareAuthService,
    provideAwareAuth,
    AwareAuthState,
    AWARE_AUTH_IDLE_CONFIG,
} from '@appbolaget/aware-auth';
import { AwareExtensionProvider, provideAwareExtension } from '@appbolaget/aware-extension';
import { AwareHttpService, provideAwareHttp, AwareHttpRequestModule } from '@appbolaget/aware-http';
import { AwareSecurityPermissionProvider, provideAwareSecurity } from '@appbolaget/aware-security';
import { environment } from '@env';
import { GodGuard, GuestGuard } from '@helpers/guards';
import { NotificationService } from '@modules/notification/notification.service';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { Store, provideStore } from '@ngxs/store';
import { MessagesService } from '@pages/messages/messages.service';
import { AppsService } from '@pages/olympus/apps/apps.service';
import { FileDownloaderService, FileUploaderService } from '@services/http';
import { DialogService } from '@viewservices/dialog.service';
import { LanguageService } from '@viewservices/language.service';
import { SnackbarService } from '@viewservices/snackbar.service';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig } from 'ngx-webstorage';
import { routes } from './app.routing';
import { Config, VersionService, PermissionProvider, ExtensionProvider } from './services';
import { AppState } from './state/app.state';
import { customStateSerializer, customStateDeserializer } from './state/state.serializer';
import { UnitState } from './state/unit.state';
import { provideDragDrop } from '@modules/dragdrop/dragdrop.module';
import { Subject, takeUntil, filter, tap, switchMap, take, delay, of, catchError } from 'rxjs';
import { SetActiveUnitIfNotSet } from './state/unit.actions';
import * as Sentry from '@sentry/angular';
import { AwareSentryProvider, provideAwareSentry } from '@appbolaget/sentry';
import { SentryProvider } from '@services/sentry.provider';
import { AwareSentryHttpInterceptor } from '@appbolaget/sentry';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { sv } from 'date-fns/locale';

const dateFnsConfig = new DateFnsConfigurationService();
dateFnsConfig.setLocale(sv);
import { IdleConfigProvider } from '@modules/auth/idle-config.provider';
import { provideNotificationModule } from '@modules/notification/notification.module';
import { PwaService } from '@modules/pwa/pwa.service';
import { provideDateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatomoService } from '@services/matomo.service';
import { SecureImagePipe } from '@helpers/pipes';
import { PostService } from '@pages/posts/posts.service';
import { ContractService } from '@modules/contract/contract.service';

const awareImageLoader = (config: ImageLoaderConfig) => {
    let url = `${environment.api.url}/@media/${config.src}&`;
    let queryParams = [];
    if (config.width) {
        queryParams.push(`w=${config.width}`);
    }

    if (config.loaderParams?.height) {
        queryParams.push(`h=${config.loaderParams.height}`);
    }

    return url + queryParams.join('&');
};

const appInit = () => {
    return async () => {
        const matIconRegistry = inject(MatIconRegistry);
        const sanitizer = inject(DomSanitizer);
        matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
        matIconRegistry.addSvgIcon('keep-pin', sanitizer.bypassSecurityTrustResourceUrl('assets/svg/pin_icon.svg'));
        matIconRegistry.addSvgIcon('keep-pin-filled', sanitizer.bypassSecurityTrustResourceUrl('assets/svg/pin_icon-filled.svg'));
        const store = inject(Store);
        const api = inject(AwareHttpService);
        const awareAuth = inject(AwareAuthService);
        inject(ContractService).initAwareContractService();
        inject(NotificationService);
        inject(Sentry.TraceService);
        inject(MatomoService);
        inject(PwaService).init();
        const initted$ = new Subject<void>();

        return await store
            .select(AppState.initialData)
            .pipe(
                takeUntil(initted$),
                filter((state) => state && state.state_init),
                tap((appState) => {
                    if (appState.api_env) {
                        api.setApiEnv(appState.api_env);
                    }
                }),
                switchMap((_) =>
                    awareAuth.state$.pipe(
                        filter((state) => state.stateChecked),
                        take(1),
                        delay(100),
                    ),
                ),
                switchMap((authState: AwareAuthState) => {
                    if (authState?.client && authState?.token && authState?.refreshToken) {
                        return store.dispatch(new SetActiveUnitIfNotSet(authState.client.units));
                    }

                    return of(true);
                }),
                catchError(() => {
                    awareAuth.logout();
                    return of(true);
                }),
                tap(() => {
                    initted$.next();
                }),
            )
            .toPromise();
    };
};

export const applicationConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(TreeModule),
        AppsService,
        PostService,
        Config,
        DialogService,
        FileDownloaderService,
        FileUploaderService,
        GodGuard,
        GuestGuard,
        MessagesService,
        VersionService,
        LanguageService,
        SnackbarService,
        SecureImagePipe,
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json?v=9',
            }),
            fallbackLang: 'sv',
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AwareAuthHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AwareSentryHttpInterceptor,
            multi: true,
        },
        { provide: LOCALE_ID, useValue: 'sv-SE' },
        { provide: MAT_DATE_LOCALE, useValue: sv },
        { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
        provideDateFnsAdapter(),
        { provide: DateFnsConfigurationService, useValue: dateFnsConfig },
        { provide: AwareSecurityPermissionProvider, useClass: PermissionProvider },
        { provide: AwareExtensionProvider, useClass: ExtensionProvider },
        { provide: AwareSentryProvider, useClass: SentryProvider },
        { provide: AWARE_AUTH_IDLE_CONFIG, useClass: IdleConfigProvider },
        // { provide: AWARE_AUTH_ASYNC_STORAGE_ENGINE, useClass: AwareAuthLocalStorageEngine, },
        {
            provide: IMAGE_LOADER,
            useValue: awareImageLoader,
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                logErrors: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(appInit()),
        provideAwareAuth({
            loginRoute: '/auth/login',
        }),
        provideAwareHttp({
            defaultEnv: {
                name: environment.envName,
                url: environment.api.url,
                unit: environment.api.unit,
            },
            defaultHeaders: {
                source: environment.api.source,
                language: 'sv',
                location: 'se',
                timezone: 'Europe/Stockholm',
                hidden: '*',
                module: AwareHttpRequestModule.Cms,
                unit: environment.api.unit,
            },
        }),
        provideStore(
            [AppState, UnitState],
            {
                developmentMode: !environment.production,
            },
            withNgxsStoragePlugin({
                keys: ['app.api_env', UnitState],
                serialize: customStateSerializer,
                deserialize: customStateDeserializer,
            }),
            withNgxsLoggerPlugin({ disabled: environment.production }),
        ),
        // provideAwareContract({
        //     unit$: () => inject(Store).select(UnitState.activeUnit),
        // }),
        provideAwareSecurity(),
        provideAwareExtension(),
        provideAwareSentry(),
        provideNotificationModule(),
        provideAnimations(),
        provideRouter(routes, withComponentInputBinding()),
        provideNgxWebstorage(withNgxWebstorageConfig({ prefix: 'aw', separator: '.', caseSensitive: true }), withLocalStorage()),
        provideHttpClient(withInterceptorsFromDi()),
        provideMatomo(
            {
                siteId: environment.matomo.siteId,
                trackerUrl: environment.matomo.trackerUrl,
                disabled: !environment.matomo.enabled,
                runOutsideAngularZone: true,
            },
            withRouter(),
        ),
        provideDragDrop(),
    ],
};
